import React, { createContext, useState, useContext, useEffect } from 'react';
import { predefinedThemes, Theme } from './predefinedThemes.ts'; // Import predefined themes
import { auth, db, storage } from './firebase.ts';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { User } from 'firebase/auth';

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeContextType {
  theme: Theme;
  setCurrentThemeName: (name: string) => void;
  predefinedThemes: Theme[];
  customLogoUrl: string | null;
  customLogoPath: string | null;
  customHeaderText: string | null;
  brandingOption: 'default' | 'text' | 'logo';
  uploadLogo: (file: File) => Promise<string | undefined>;
  updateCustomHeaderText: (text: string) => void;
  clearCustomLogo: () => Promise<void>;
  clearCustomHeaderText: () => void;
  setBrandingOption: (option: 'default' | 'text' | 'logo') => Promise<void>;
  isSchedulePageVisible: boolean;
  setIsSchedulePageVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentThemeName, setCurrentThemeName] = useState<string>(() => {
    try {
      const savedThemeName = localStorage.getItem('appThemeName');
      const defaultThemeName = predefinedThemes[0].name;
      return savedThemeName || defaultThemeName;
    } catch (error) {
      console.error("Failed to load theme name from localStorage:", error);
      return predefinedThemes[0].name;
    }
  });

  const theme = predefinedThemes.find((t: Theme) => t.name === currentThemeName) || predefinedThemes[0];
  const [customLogoUrl, setCustomLogoUrl] = useState<string | null>(null);
  const [customLogoPath, setCustomLogoPath] = useState<string | null>(null);
  const [customHeaderText, setCustomHeaderText] = useState<string | null>(() => {
    try {
      return localStorage.getItem('customHeaderText') || null;
    } catch (error) {
      console.error("Failed to load custom header text from localStorage:", error);
      return null;
    }
  });
  const [brandingOption, setBrandingOption] = useState<'default' | 'text' | 'logo'>(() => {
    try {
      return (localStorage.getItem('brandingOption') as 'default' | 'text' | 'logo') || 'default';
    } catch (error) {
      console.error("Failed to load branding option from localStorage:", error);
      return 'default';
    }
  });
  const [isSchedulePageVisible, setIsSchedulePageVisible] = useState<boolean>(() => {
    try {
      const savedVisibility = localStorage.getItem('isSchedulePageVisible');
      return savedVisibility !== null ? JSON.parse(savedVisibility) : true;
    } catch (error) {
      console.error("Failed to load schedule page visibility from localStorage:", error);
      return true;
    }
  });
  const [userId, setUserId] = useState<string | null>(null);

  // Listen for auth state changes to get the user ID
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user: User | null) => {
      if (user) {
        setUserId(user.uid);
      } else {
        setUserId(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Effect to update the theme when currentThemeName changes
  useEffect(() => {
    // Save theme name to localStorage whenever it changes
    try {
      localStorage.setItem('appThemeName', currentThemeName);
    } catch (error) {
      console.error("Failed to save theme name to localStorage:", error);
    }
    // Apply CSS variables to the document root
    applyThemeToCss(theme);
  }, [currentThemeName, theme]); // Depend on currentThemeName and theme

  // Effect to save branding option, custom header text, and schedule page visibility to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('brandingOption', brandingOption);
      if (customHeaderText !== null) {
        localStorage.setItem('customHeaderText', customHeaderText);
      } else {
        localStorage.removeItem('customHeaderText');
      }
      localStorage.setItem('isSchedulePageVisible', JSON.stringify(isSchedulePageVisible));
    } catch (error) {
      console.error("Failed to save branding option, custom header text, or schedule page visibility to localStorage:", error);
    }
  }, [brandingOption, customHeaderText, isSchedulePageVisible]);

  // Effect to load user-specific theme and white-labeling preferences from Firestore
  useEffect(() => {
    if (userId) {
      const userDocRef = doc(db, 'users', userId);
      const unsubscribe = onSnapshot(userDocRef, (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          if (userData) {
            if (userData.themePreference) {
              setCurrentThemeName(userData.themePreference);
            }
            if (userData.customLogoUrl) {
              setCustomLogoUrl(userData.customLogoUrl);
            }
            if (userData.customLogoPath) { // Load customLogoPath
              setCustomLogoPath(userData.customLogoPath);
            }
            // Prioritize Firestore data for customHeaderText and brandingOption
            if (userData.customHeaderText !== undefined) {
              setCustomHeaderText(userData.customHeaderText);
            }
            if (userData.brandingOption !== undefined) {
              setBrandingOption(userData.brandingOption);
            }
            if (userData.isSchedulePageVisible !== undefined) {
              setIsSchedulePageVisible(userData.isSchedulePageVisible);
            }
          }
        }
      }, (error) => {
        console.error("Error fetching user preferences:", error);
      });
      return () => unsubscribe();
    }
  }, [userId]);

  // Effect to save user-specific theme and white-labeling preferences to Firestore
  useEffect(() => {
    if (userId) {
      const userDocRef = doc(db, 'users', userId);
      const dataToSave: { [key: string]: any } = {}; // Explicitly type as indexable object
      if (currentThemeName !== undefined) {
        dataToSave.themePreference = currentThemeName;
      }
      if (customLogoUrl !== null) {
        dataToSave.customLogoUrl = customLogoUrl;
      } else {
        dataToSave.customLogoUrl = null; // Explicitly set to null if cleared
      }
      if (customLogoPath !== null) { // Save customLogoPath
        dataToSave.customLogoPath = customLogoPath;
      } else {
        dataToSave.customLogoPath = null; // Explicitly set to null if cleared
      }
      // Ensure customHeaderText is saved as an empty string if it's empty, not null
      if (customHeaderText !== null) {
        dataToSave.customHeaderText = customHeaderText;
      } else {
        dataToSave.customHeaderText = null; // Explicitly set to null if cleared
      }
      if (brandingOption !== undefined) {
        dataToSave.brandingOption = brandingOption;
      }
      if (isSchedulePageVisible !== undefined) {
        dataToSave.isSchedulePageVisible = isSchedulePageVisible;
      }

      if (Object.keys(dataToSave).length > 0) {
        setDoc(userDocRef, dataToSave, { merge: true })
          .catch(error => console.error("Error saving preferences:", error));
      }
    }
  }, [userId, currentThemeName, customLogoUrl, customLogoPath, customHeaderText, brandingOption, isSchedulePageVisible]);

  const uploadLogo = async (file: File): Promise<string | undefined> => {
    if (!userId || !file) return undefined;

    const path = `users/${userId}/logo/${file.name}`;
    const logoRef = ref(storage, path);
    try {
      await uploadBytes(logoRef, file);
      const url = await getDownloadURL(logoRef);
      setCustomLogoUrl(url);
      setCustomLogoPath(path); // Save the path
      return url;
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw error;
    }
  };

  const updateCustomHeaderText = (text: string) => {
    setCustomHeaderText(text);
  };

  const clearCustomLogo = async () => {
    if (customLogoPath && userId) { // Ensure userId exists for deletion
      try {
        const logoRef = ref(storage, customLogoPath);
        await deleteObject(logoRef);
        console.log("Logo deleted from storage:", customLogoPath);
      } catch (error) {
        console.error("Error deleting logo from storage:", error);
        // Continue to clear URL and path even if deletion fails
      }
    }
    setCustomLogoUrl(null);
    setCustomLogoPath(null);
  };

  const clearCustomHeaderText = () => {
    setCustomHeaderText(null);
  };

  const setBrandingOptionAndClear = async (option: 'default' | 'text' | 'logo') => {
    setBrandingOption(option);
    if (option === 'default') {
      await clearCustomLogo();
      clearCustomHeaderText();
    } else if (option === 'text') {
      await clearCustomLogo();
    } else if (option === 'logo') {
      clearCustomHeaderText();
    }
  };

  const applyThemeToCss = (currentTheme: Theme) => {
    const root = document.documentElement;
    // Apply colors
    for (const colorName in currentTheme.colors) {
      root.style.setProperty(`--theme-colors-${colorName}`, (currentTheme.colors as any)[colorName]);
    }
    // Apply fonts
    for (const fontName in currentTheme.fonts) {
      root.style.setProperty(`--theme-fonts-${fontName}`, (currentTheme.fonts as any)[fontName]);
    }
    // Apply spacing
    for (const spacingName in currentTheme.spacing) {
      root.style.setProperty(`--theme-spacing-${spacingName}`, (currentTheme.spacing as any)[spacingName]);
    }
    // Apply other properties
    root.style.setProperty('--theme-borderRadius', currentTheme.borderRadius);
    root.style.setProperty('--theme-boxShadow', currentTheme.boxShadow);
  };

  // Initial application of theme when component mounts
  useEffect(() => {
    applyThemeToCss(theme);
  }, []); // Empty dependency array means this runs once on mount

  return (
    <ThemeContext.Provider value={{
      theme,
      setCurrentThemeName,
      predefinedThemes,
      customLogoUrl,
      customLogoPath, // Provide customLogoPath
      customHeaderText,
      brandingOption, // Provide brandingOption
      uploadLogo,
      updateCustomHeaderText,
      clearCustomLogo,
      clearCustomHeaderText,
      setBrandingOption: setBrandingOptionAndClear, // Provide the new function
      isSchedulePageVisible,
      setIsSchedulePageVisible
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
